<template>
  <div v-if="!hasConsented" class="cookie-consent-banner">
    <div class="cookie-content">
      <p>We use cookies and similar technologies to enhance your experience, analyze our traffic, and for security. By
        clicking "Accept", you consent to our use of cookies and to receive notifications.</p>
    </div>
    <div class="cookie-actions">
      <button @click="acceptCookies" class="btn-primary">Accept All</button>
      <button @click="rejectCookies" class="btn-secondary">Decline</button>
    </div>
  </div>
</template>

<script>
import { app } from '@/config/firebase';
import { getMessaging, isSupported, getToken } from 'firebase/messaging';
import { initClarity } from '@/config/clarity';

export default {
  name: "CookieConsent",
  data() {
    return {
      hasConsented: true, // assume true until we check localStorage on mount
    };
  },
  mounted() {
    const consent = localStorage.getItem('cookieConsent');
    if (consent === null) {
      this.hasConsented = false;
    } else if (consent === 'true') {
      // Already consented, initialize analytics and notifications quietly
      this.initAnalytics();
    }
  },
  methods: {
    async acceptCookies() {
      localStorage.setItem('cookieConsent', 'true');
      this.hasConsented = true;
      await this.initAnalytics();
    },
    rejectCookies() {
      localStorage.setItem('cookieConsent', 'false');
      this.hasConsented = true;
    },
    async initAnalytics() {
      // 1. Initialize Clarity
      if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_CLARITY_PROJECT_ID) {
        initClarity(process.env.VUE_APP_CLARITY_PROJECT_ID);
      }

      // 2. Initialize Firebase Messaging
      if (!app) {
        console.warn('[Analytics] Firebase app is not initialized.');
        return;
      }
      try {
        const supported = await isSupported();
        if (!supported) return;

        const messaging = getMessaging(app);
        const permission = await Notification.requestPermission();

        if (permission !== 'granted') {
          console.warn('[Analytics] Notification permission was denied by the user.');
          return;
        }

        const vapidKey = process.env.VUE_APP_FIREBASE_VAPID_KEY;
        if (!vapidKey) return;

        let registration;
        try {
          registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        } catch (swError) {
          console.error('[Analytics] SW registration failed', swError);
          return;
        }

        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: registration
        });

        if (token) {
          console.info('[Analytics] FCM token obtained successfully.');
        } else {
          console.warn('[Analytics] No FCM token received.');
        }
      } catch (error) {
        console.error('[Analytics] Firebase messaging setup failed:', error);
      }
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap");

.cookie-consent-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #1e2d3a;
  color: #fff;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  font-family: 'Sora', sans-serif;
}

.cookie-content p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #f2f6f8;
}

.cookie-actions {
  display: flex;
  gap: 12px;
  margin-left: 24px;
  flex-shrink: 0;
}

.cookie-actions button {
  padding: 8px 20px;
  border-radius: 6px;
  font-family: 'Sora', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #00968f;
  border: 1px solid #00968f;
  color: white;
}

.btn-primary:hover {
  background-color: #007a74;
}

.btn-secondary {
  background-color: transparent;
  border: 1px solid #c4d4df;
  color: white;
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

@media (max-width: 768px) {
  .cookie-consent-banner {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .cookie-actions {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
